import React, { useState } from "react";
import Input from "../../../components/common/Input";
import Button from "../../../components/common/Button";
import useAuth from "../../../hooks/useAuth";
import { signupAPI } from "../../../api/index";
import "./SignupModal.css";

const SignupModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    phone: "",
    email: "",
    name: "",
    companyName: "",
    businessFile: null,
  });

  const { getUserInfo } = useAuth();
  const [errors, setErrors] = useState({});
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        businessFile: file,
      }));
      setSelectedFileName(file.name);
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      confirmPassword: "",
      phone: "",
      email: "",
      name: "",
      companyName: "",
      businessFile: null,
    });
    setErrors({});
    setIsUsernameAvailable(null);
    setSelectedFileName("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const formatPhoneNumber = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/[^0-9]/g, "");

    // 길이에 따라 하이픈 추가
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(
        7,
        11
      )}`;
    }
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;

    // 휴대폰번호 필드인 경우 자동 포맷팅
    if (field === "phone") {
      processedValue = formatPhoneNumber(value);
    } else {
      // 다른 필드는 trim하지 않고 그대로 저장
      processedValue = value;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: processedValue,
    }));

    // username이 변경되면 중복 확인 상태 초기화
    if (field === "username") {
      setIsUsernameAvailable(null);
    }

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleBlur = (field) => {
    // 휴대폰번호는 포맷팅을 유지하므로 trim하지 않음
    if (field === "phone") {
      return;
    }

    // 포커스가 벗어날 때 trim 적용
    const trimmedValue = formData[field].trim();

    setFormData((prev) => ({
      ...prev,
      [field]: trimmedValue,
    }));
  };

  const checkUsernameAvailability = async () => {
    const trimmedUsername = formData.username.trim();

    if (!trimmedUsername) {
      alert("아이디를 먼저 입력해주세요.");
      return;
    }

    // username 검증
    if (trimmedUsername.length < 8) {
      alert("아이디는 8자 이상이어야 합니다.");
      return;
    }

    if (!/^[a-zA-Z0-9]+$/.test(trimmedUsername)) {
      alert("아이디는 영문과 숫자만 사용 가능합니다.");
      return;
    }

    try {
      const result = await getUserInfo(trimmedUsername);

      if (result.success && result.isAvailable) {
        // 아이디 사용 가능
        setIsUsernameAvailable(true);
        alert("사용 가능한 아이디입니다.");
      } else {
        // 아이디 중복 또는 오류
        setIsUsernameAvailable(false);
        alert(result.message || "중복 확인 중 오류가 발생했습니다.");
      }
    } catch (error) {
      console.error("중복 확인 오류:", error);
      setIsUsernameAvailable(false);
      alert("중복 확인 중 오류가 발생했습니다.");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // 모든 필드에 trim 적용 (휴대폰번호는 하이픈 제거)
    const trimmedData = {
      username: formData.username.trim(),
      password: formData.password.trim(),
      confirmPassword: formData.confirmPassword.trim(),
      phone: formData.phone.replace(/[^0-9]/g, ""), // 하이픈 제거
      email: formData.email.trim(),
      name: formData.name.trim(),
      companyName: formData.companyName.trim(),
    };

    // 필수 필드 검증
    if (!trimmedData.username) {
      newErrors.username = "아이디를 입력해주세요";
    } else if (trimmedData.username.length < 8) {
      newErrors.username = "아이디는 8자 이상이어야 합니다";
    } else if (!/^[a-zA-Z0-9]+$/.test(trimmedData.username)) {
      newErrors.username = "아이디는 영문과 숫자만 사용 가능합니다";
    } else if (isUsernameAvailable === null) {
      newErrors.username = "아이디 중복 확인을 해주세요";
    } else if (isUsernameAvailable === false) {
      newErrors.username = "중복된 아이디입니다. 다른 아이디를 사용해주세요";
    }

    if (!trimmedData.password) {
      newErrors.password = "비밀번호를 입력해주세요";
    } else if (trimmedData.password.length < 6) {
      newErrors.password = "비밀번호는 6자 이상이어야 합니다";
    }

    if (trimmedData.password !== trimmedData.confirmPassword) {
      newErrors.confirmPassword = "비밀번호를 다시 입력해주세요";
    }
    if (!trimmedData.name) {
      newErrors.name = "이름을 입력해주세요";
    }

    if (!trimmedData.phone) {
      newErrors.phone = "휴대폰번호를 입력해주세요";
    } else if (trimmedData.phone.length < 10) {
      newErrors.phone = "올바른 휴대폰번호를 입력해주세요";
    } else if (!/^01[0-9]/.test(trimmedData.phone)) {
      newErrors.phone = "010, 011, 012, 019로 시작하는 번호를 입력해주세요";
    }

    if (!trimmedData.email) {
      newErrors.email = "이메일을 입력해주세요";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedData.email)) {
      newErrors.email = "올바른 이메일 형식을 입력해주세요";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      // API 키값에 맞게 데이터 변환
      const apiData = {
        username: formData.username.trim(),
        password: formData.password.trim(),
        name: formData.name.trim(),
        phone: formData.phone.replace(/[^0-9]/g, ""), // 하이픈 제거
        email: formData.email.trim(),
        company: formData.companyName.trim() || undefined,
        business_license_url: formData.businessFile ? "https://..." : undefined, // 실제 파일 업로드 URL로 변경 필요
      };

      try {
        const result = await signupAPI(apiData);

        if (result === "") {
          alert("회원가입이 완료되었습니다!");
          handleClose();
        } else {
          alert(`회원가입 실패: ${result}`);
        }
      } catch (error) {
        console.error("회원가입 오류:", error);
        alert("회원가입 중 오류가 발생했습니다.");
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="signup-modal-overlay">
      <div
        className="signup-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="signup-modal-header">
          <h2 className="signup-modal-title">회원가입</h2>
          <button className="signup-modal-close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <form className="signup-modal-form" onSubmit={handleSubmit}>
          <div className="signup-form-layout">
            {/* 1행: 아이디 */}
            <div className="signup-form-row">
              <div className="signup-form-field">
                <div className="signup-form-label">아이디</div>
                <div className="signup-form-input-item">
                  <div className="signup-input-group">
                    <Input
                      type="text"
                      value={formData.username}
                      onChange={(e) =>
                        handleInputChange("username", e.target.value)
                      }
                      onBlur={() => handleBlur("username")}
                      placeholder="Enter"
                      error={errors.username}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="small"
                      onClick={checkUsernameAvailability}
                      disabled={isUsernameAvailable !== null}
                    >
                      {isUsernameAvailable === null ? "중복확인" : "확인완료"}
                    </Button>
                  </div>
                  {isUsernameAvailable === true && (
                    <div className="signup-success-message">
                      사용 가능한 아이디입니다.
                    </div>
                  )}
                  {isUsernameAvailable === false && (
                    <div className="signup-error-message">
                      중복된 아이디입니다.
                    </div>
                  )}
                </div>
              </div>
              <div className="signup-form-field"></div>
            </div>

            {/* 2행: 비밀번호 + 비밀번호 확인 */}
            <div className="signup-form-row">
              <div className="signup-form-field">
                <div className="signup-form-label">비밀번호</div>
                <div className="signup-form-input-item">
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    onBlur={() => handleBlur("password")}
                    placeholder="Enter"
                    error={errors.password}
                  />
                </div>
              </div>

              <div className="signup-form-field">
                <div className="signup-form-label">비밀번호 확인</div>
                <div className="signup-form-input-item">
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      handleInputChange("confirmPassword", e.target.value)
                    }
                    onBlur={() => handleBlur("confirmPassword")}
                    placeholder="Enter"
                    error={errors.confirmPassword}
                  />
                </div>
              </div>
            </div>

            {/* 3행: 휴대폰번호 + 이메일 */}
            <div className="signup-form-row">
              <div className="signup-form-field">
                <div className="signup-form-label">휴대폰번호</div>
                <div className="signup-form-input-item">
                  <Input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    onBlur={() => handleBlur("phone")}
                    placeholder="010-1234-5678"
                    error={errors.phone}
                    maxLength={13}
                  />
                </div>
              </div>

              <div className="signup-form-field">
                <div className="signup-form-label">Email</div>
                <div className="signup-form-input-item">
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onBlur={() => handleBlur("email")}
                    placeholder="qwer1234@naver.com"
                    error={errors.email}
                  />
                </div>
              </div>
            </div>

            {/* 4행: 대표자명 + 업체명 */}
            <div className="signup-form-row">
              <div className="signup-form-field">
                <div className="signup-form-label">이름</div>
                <div className="signup-form-input-item">
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    onBlur={() => handleBlur("name")}
                    placeholder="홍길동"
                    error={errors.name}
                  />
                </div>
              </div>

              <div className="signup-form-field">
                <div className="signup-form-label">업체명(선택)</div>
                <div className="signup-form-input-item">
                  <Input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) =>
                      handleInputChange("companyName", e.target.value)
                    }
                    onBlur={() => handleBlur("companyName")}
                    placeholder="(주)컴퍼니"
                    error={errors.companyName}
                  />
                </div>
              </div>
            </div>

            {/* 5행: 사업자 등록증 업로드 */}
            {/* <div className="signup-form-row">
              <div className="signup-form-field">
                <div className="signup-form-label">사업자 등록증 업로드(선택)</div>
                <div className="signup-form-input-item">
                  <div className="signup-file-input-group">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="signup-file-input"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="small"
                      onClick={() =>
                        document.querySelector(".signup-file-input").click()
                      }
                    >
                      파일 선택
                    </Button>
                  </div>
                  {selectedFileName && (
                    <div className="signup-success-message">
                      선택된 파일: {selectedFileName}
                    </div>
                  )}
                </div>
              </div>
              <div className="signup-form-field"></div>
            </div> */}
          </div>

          <div className="signup-form-actions">
            <Button
              type="submit"
              variant="primary"
              size="large"
              className="signup-btn"
            >
              회원가입
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="large"
              onClick={handleClose}
            >
              닫기
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupModal;
